import {
  Button,
  CalendarIconRed,
  Div,
  HeaderIconWrapper,
  lightTheme,
  ModalPortal,
} from '@vgrubs/components';
import 'flatpickr/dist/flatpickr.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import { AnimatePresence } from 'framer-motion';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  AdminDatePickerStyled,
  FlatpickrStyled,
} from './AdminDatePickerStyled';

export const AdminDatePickerModal = ({
  Icon,
  value,
  onChange,
  click,
  dateFrom,
  dateTo,
  monthPicker = false,
  mode = 'range',
  header,
  hasIcon = true,
  disabledDates = [],
  dateFormat = 'M d, Y',
  triggerComponent,
  minDate,
  maxDate,
  customComponent,
  $marker,
  isOpen,
  setIsOpen,
  ...props
}: {
  Icon?: React.ReactNode;
  value: string[] | string | Date[] | Date | (Date | string)[];
  onChange: (date: Date[] | string[]) => void | undefined;
  click: () => void;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  monthPicker?: boolean;
  mode?: 'range' | 'single';
  header?: string;
  hasIcon?: boolean;
  disabledDates?: string[];
  dateFormat?: string;
  triggerComponent?: React.ReactNode;
  minDate?: string;
  maxDate?: string;
  customComponent?: React.ReactNode;
  $marker?: boolean;
  isOpen?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}) => {
  // Through ThemeProvider so a navy-primary app recolors the calendar glyph;
  // admin's theme.primary is the same red this icon always carried.
  const theme = useTheme();
  const [internalModal, setInternalModal] = useState(false);
  const isControlled = setIsOpen !== undefined;
  const modal = isControlled ? !!isOpen : internalModal;
  const setModal = isControlled ? setIsOpen : setInternalModal;

  const monthPickerPlugin = monthPicker
    ? monthSelectPlugin({
        shorthand: true,
        dateFormat: 'M d, Y',
        theme: 'light',
      })
    : null;

  return (
    <AnimatePresence>
      <Div $relative>
        {hasIcon &&
          (triggerComponent ? (
            <div onClick={() => setModal((prev) => !prev)}>
              {triggerComponent}
            </div>
          ) : (
            <HeaderIconWrapper
              $opened={modal}
              $marker={$marker}
              onClick={() => setModal((prev) => !prev)}
            >
              {Icon ? Icon : <CalendarIconRed color={theme.primary} />}
            </HeaderIconWrapper>
          ))}
        <ModalPortal
          isOpen={modal}
          closeModal={() => setModal(false)}
          width={'auto'}
          backgroundColor={lightTheme.white}
          padding="16px"
          paddingSM="12px"
          header={header}
          exitButton={false}
          overflow="visible"
        >
          <FlatpickrStyled>
            <AdminDatePickerStyled
              value={value}
              defaultValue={value as string}
              onChange={onChange}
              {...props}
              options={{
                mode: monthPicker ? 'single' : mode,
                dateFormat,
                inline: true,
                locale: { rangeSeparator: ' - ' },
                plugins: monthPicker ? [monthPickerPlugin!] : [],
                disable: disabledDates,
                minDate: minDate,
                maxDate: maxDate,
              }}
              className="form-control"
            />
          </FlatpickrStyled>

          {customComponent}

          <Button
            $small
            $selected
            $w="100%"
            $mt="20"
            $wSM="100%"
            onClick={() => {
              click();
              setModal(false);
            }}
          >
            Apply
          </Button>
        </ModalPortal>
      </Div>
    </AnimatePresence>
  );
};
